import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSHomeData,
  type EnrollmentHYCSHomeSubmitInput,
  type EnrollmentHYCSHomeSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS Home data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSHomeData>
 * @example const response = await getEnrollmentHYCSHomeData()
 */
export const getEnrollmentHYCSHomeData = async (
  enrollmentHomeSubmitInput?: EnrollmentHYCSHomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSHomeData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSHomeData',
      query: `mutation getEnrollmentHYCSHomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentHomeSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSHomeData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSHomeData = createQueryHook<EnrollmentHYCSHomeSubmitInput, EnrollmentHYCSHomeData>(
  getEnrollmentHYCSHomeData,
)

/**
 * Submit Enrollment HYCS Home data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSHomeSubmitOutput>
 * @example const response = await submitEnrollmentHYCSHomeData();
 */
export const submitEnrollmentHYCSHomeData = async (
  enrollmentHomeSubmitInput: EnrollmentHYCSHomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSHomeSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSHomeData',
      query: `mutation submitEnrollmentHYCSHomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentHomeSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSHomeSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSHomeData = createMutationHook<
  EnrollmentHYCSHomeSubmitInput,
  EnrollmentHYCSHomeSubmitOutput
>(submitEnrollmentHYCSHomeData)
